/**
 * Source by path, not by prompt.
 *
 * The origination of a record is decided by the code path that creates it.
 * Only a bare manual add has no path, and it defaults to the creator's own
 * network. Every create route calls this so the rule lives in one place.
 */
import type { OriginationInput } from "./originationConstants";

export type CreationPath =
  | "website_valuation"
  | "outreach_promote"
  | "from_coi"
  | "from_advocate"
  | "chrome_extension"
  | "mcp"
  | "manual"
  | "outlook"
  | "business_card"
  | "bulk_upload"
  | "onepager";

export interface PathContext {
  userId?: string | null;
  submissionId?: string | null;
  outreachListId?: string | null;
  campaignId?: string | null;
  contactId?: string | null;
  note?: string | null;
  /** YYYY-MM-DD, the caller's today. Passed in so the function stays pure. */
  today: string;
}

export function originationByPath(path: CreationPath, ctx: PathContext): OriginationInput {
  const occurredOn = ctx.today;
  switch (path) {
    case "website_valuation":
      return { sourceType: "inbound_website", submissionId: ctx.submissionId ?? null, occurredOn };
    case "outreach_promote":
      return { sourceType: "outbound", outreachListId: ctx.outreachListId ?? null, campaignId: ctx.campaignId ?? null, occurredOn };
    case "from_coi":
      return { sourceType: "coi_referral", contactId: ctx.contactId ?? null, note: ctx.note ?? null, occurredOn };
    case "from_advocate":
      return { sourceType: "advocate_referral", contactId: ctx.contactId ?? null, note: ctx.note ?? null, occurredOn };
    case "chrome_extension":
    case "mcp":
      return { sourceType: "outbound", detail: "research", occurredOn };
    case "manual":
    case "outlook":
    case "business_card":
    case "bulk_upload":
    case "onepager":
      return ctx.userId
        ? { sourceType: "partner_network", userId: ctx.userId, occurredOn }
        : { sourceType: "other", detail: "Not recorded", occurredOn };
  }
}

/** companies.creation_source -> the path it implies. */
export function pathFromCreationSource(cs: string | null | undefined): CreationPath {
  switch (cs) {
    case "chrome_extension": return "chrome_extension";
    case "mcp": return "mcp";
    case "outlook": return "outlook";
    case "business_card": return "business_card";
    case "bulk_upload": return "bulk_upload";
    case "onepager": return "onepager";
    case "website": return "website_valuation";
    default: return "manual";
  }
}

/** Today as YYYY-MM-DD in UTC. Railway runs UTC; a date is what is stored. */
export function todayIso(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}
